import { Formik } from 'formik';
import { useState } from 'react';
import { updateConfig } from './service';
import { Alert, Button, Form, InputNumber, Radio, Select, message } from 'antd';
import { isNil } from 'lodash-es';
import styled from '@emotion/styled';
import { Inline } from 'views/Aide/Scenario/TaskForm/TimeoutReplyField';
import TransComponent from 'components/TransComponent';
import { T, useTranslate } from '@tolgee/react';

type FV = NightWatch.StoreGroupConfig['fallbackLimit'];

export interface LimitFormProps {
  editable?: boolean;
  config: NightWatch.StoreGroupConfig;
  onUpdated: () => Promise<any>;
}

function LimitForm(props: LimitFormProps) {
  const { editable = true, config, onUpdated } = props;

  const { t } = useTranslate();

  const [editing, setEditing] = useState(false);

  return (
    <Formik<FV>
      enableReinitialize
      initialValues={config.fallbackLimit}
      onSubmit={(values, helpers) => {
        return updateConfig({
          ...config,
          fallbackLimit: values,
        })
          .then(() => onUpdated())
          .then(() => {
            setEditing(false);

            helpers.resetForm();

            message.success(t('operation.saveSucceed'));
          })
          .catch(() => {
            message.error(t('operation.saveFailed'));
          });
      }}
    >
      {(props) => {
        const { values, setValues, isSubmitting, resetForm, handleSubmit } = props;

        const { enable, timePeriod, timeUnit, count } = values;

        return (
          <Container>
            <header>
              <h4>{t('aide.值守兜底回复限制')}</h4>
              {editing ? (
                <div>
                  <Button
                    type="link"
                    onClick={() => {
                      resetForm();
                      setEditing(false);
                    }}
                  >
                    {t('translation.取消')}
                  </Button>
                  <Button type="link" loading={isSubmitting} onClick={() => handleSubmit()}>
                    {t('marketing.保存')}
                  </Button>
                </div>
              ) : (
                <Button disabled={!editable} type="link" onClick={() => setEditing(true)}>
                  {t('marketing.编辑')}
                </Button>
              )}
            </header>
            <Form.Item label={t('aide.是否限制')}>
              <div style={{ padding: '5px 0' }}>
                <Radio
                  disabled={!editing}
                  checked={enable}
                  onChange={({ target: { checked } }) => {
                    if (checked) {
                      setValues({
                        ...values,
                        enable: true,
                      });
                    }
                  }}
                >
                  {t('aide.限制')}
                </Radio>
                <Radio
                  disabled={!editing}
                  checked={!enable}
                  onChange={({ target: { checked } }) => {
                    if (checked) {
                      setValues({
                        ...values,
                        enable: false,
                      });
                    }
                  }}
                >
                  {t('aide.不限制')}
                </Radio>
              </div>
              {enable ? (
                <div style={{ padding: '12px 16px', background: 'var(--fett-bg-subtle)' }}>
                  <Inline>
                    <T
                      keyName="aide.watchLimit"
                      params={{
                        x: (
                          <TransComponent
                            content={
                              <>
                                <InputNumber
                                  disabled={!editing}
                                  min={1}
                                  max={
                                    {
                                      MINUTES: 120,
                                      HOURS: 12,
                                    }[timeUnit]
                                  }
                                  value={timePeriod}
                                  onChange={(timePeriod) => {
                                    if (!isNil(timePeriod)) {
                                      setValues({ ...values, timePeriod });
                                    }
                                  }}
                                />
                                <Select
                                  disabled={!editing}
                                  value={timeUnit}
                                  onChange={(timeUnit) => {
                                    setValues({
                                      ...values,
                                      timeUnit,
                                      timePeriod:
                                        timeUnit === 'HOURS' && timePeriod > 12
                                          ? 12
                                          : values.timePeriod,
                                    });
                                  }}
                                  options={[
                                    { value: 'MINUTES', label: t('marketing.分钟') },
                                    { value: 'HOURS', label: t('marketing.小时') },
                                  ]}
                                  style={{ width: 'auto' }}
                                />
                              </>
                            }
                          />
                        ),
                        y: (
                          <TransComponent
                            content={
                              <InputNumber
                                disabled={!editing}
                                min={1}
                                max={100}
                                value={count}
                                onChange={(count) => {
                                  if (!isNil(count)) {
                                    setValues({ ...values, count });
                                  }
                                }}
                              />
                            }
                          />
                        ),
                      }}
                    />
                  </Inline>
                  <div
                    style={{
                      marginTop: '6px',
                      fontSize: '12px',
                      color: 'var(--fett-text-tertiary)',
                    }}
                  >
                    {t('aide.watchLimitTips', {
                      x: `${timePeriod}${
                        {
                          MINUTES: t('marketing.分钟'),
                          HOURS: t('marketing.小时'),
                        }[timeUnit]
                      }`,
                      y: count,
                    })}
                  </div>
                </div>
              ) : (
                <Alert
                  banner
                  showIcon={false}
                  type="warning"
                  message={t('aide.watchLimitCloseTips')}
                />
              )}
            </Form.Item>
          </Container>
        );
      }}
    </Formik>
  );
}

export const Container = styled.div`
  background: var(--fett-bg-panel);
  border: solid 1px var(--fett-border-default);
  > header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 48px;
    padding: 0 16px;
    border-bottom: solid 1px var(--fett-border-default);
    > h4 {
      margin-bottom: 0;
    }
    > div {
      display: flex;
      align-items: center;
    }
  }
  > div {
    padding: 16px;
  }
`;

export default LimitForm;
